import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { stats } from "../data/site";

const chapters = [
  {
    num: "01",
    title: "1971 — Foundations",
    text: "Mr. Prabhakar Ganapatrao Pathare founds the practice with piling and foundation work — the unseen strength beneath Bombay's institutions, from Bombay Port Trust to K.E.M. Hospital, Pune.",
  },
  {
    num: "02",
    title: "Construction, at scale",
    text: "The firm grows into full construction — hospitals, schools, churches and residences across Mumbai, Pune, Chennai and Saudi Arabia, delivered time-bound and without compromise.",
  },
  {
    num: "03",
    title: "Redevelopment, today",
    text: "Rajan Pathare brought Parsn into redevelopment in 2007. The legacy continues with ongoing redevelopments in various parts of Mumbai including Kurla and Chembur, delivering renewed homes and dreams to home buyers in modern homes.",
  },
];

const beliefs = [
  { label: "Vision", text: "To be a world-class creator of sustainable and quality infrastructure and construction projects." },
  { label: "Mission", text: "To achieve completion of projects in a time-bound manner without compromising on quality and benefits to the stakeholders." },
  { label: "Values", text: "Client centric. Commitment. Excellence." },
];

const Chairman = () => (
  <section data-testid="chairman-section" className="bg-paper border-b border-gray-200 py-24 sm:py-32 overflow-hidden">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
      <Reveal className="lg:col-span-7 order-2 lg:order-1">
        <div className="flex items-center gap-6 mb-10">
          <div className="w-24 h-28 sm:w-28 sm:h-32 shrink-0 overflow-hidden border border-gray-200 shadow-ambient">
            <img src="/assets/people/rajan-pathare.webp" alt="Rajan Pathare, Chairman of Parsn Group" className="w-full h-full object-cover object-top" />
          </div>
          <div>
            <p className="overline-label">A message from the Chairman</p>
          </div>
        </div>
        <blockquote className="font-serif text-2xl sm:text-4xl text-ink tracking-tight leading-snug max-w-2xl">
          "Our work has taught us to forge strong foundations — not just in our projects, but with
          all the people we've been <span className="italic text-navy">associated with.</span>"
        </blockquote>
        <p className="mt-10 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-gray-400">
          <span className="w-8 h-px bg-brand" aria-hidden="true" /> Rajan Pathare · Chairman
        </p>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-5 order-1 lg:order-2">
        <figure className="text-center">
          <img
            src="/assets/people/pg-pathare.webp"
            alt="Prabhakar Ganapatrao Pathare, founder of Parsn Group"
            loading="lazy"
            className="w-full max-w-sm mx-auto [mask-image:radial-gradient(ellipse_72%_62%_at_50%_42%,black_52%,transparent_78%)]"
          />
          <figcaption className="mt-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-gray-400">
            P.G. Pathare · Founder, 1971
          </figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  return (
    <main data-testid="about-page">
      <SEO
        title="About — Parsn Group"
        description="Founded in 1971 by Mr. Prabhakar Ganapatrao Pathare, Parsn has grown from foundation specialists into a full construction and redevelopment powerhouse."
        path="/about"
      />
      <section className="bg-white pt-32 sm:pt-44 pb-16 sm:pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="overline-label mb-4">About Parsn</p>
            <h1 className="font-serif text-4xl sm:text-6xl text-ink tracking-tight leading-[1.05] max-w-3xl">
              <span className="italic text-navy">Fifty years of building, and on.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-gray-600 leading-relaxed">
              Parsn began in 1971 and has grown from foundation specialists into a full
              construction and redevelopment powerhouse following the discipline it was founded on.
            </p>
          </Reveal>
        </div>
      </section>

      <Chairman />

      <section className="bg-paper border-y border-gray-200 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 flex flex-col gap-16 sm:gap-24">
          {chapters.map((c) => (
            <Reveal key={c.num} className="grid md:grid-cols-12 gap-6 md:gap-10 items-start">
              <span className="font-serif text-6xl sm:text-8xl text-navy/10 leading-none md:col-span-3 select-none">{c.num}</span>
              <div className="md:col-span-9 lg:col-span-7">
                <h2 className="font-serif text-2xl sm:text-4xl text-navy tracking-tight">{c.title}</h2>
                <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-12 sm:mb-16">
            <p className="overline-label mb-4">What guides us</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-ink tracking-tight leading-tight max-w-2xl">Vision, mission &amp; values</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            {beliefs.map((v, i) => (
              <Reveal key={v.label} delay={i * 0.08} className="bg-white p-8 sm:p-12 h-full">
                <p className="overline-label mb-5 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand" aria-hidden="true" /> {v.label}
                </p>
                <p className="font-serif text-xl sm:text-2xl text-ink leading-snug tracking-tight">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-gray-200">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06} className="border-b border-r border-gray-200 p-6 sm:p-10">
                <p className="font-serif text-4xl sm:text-5xl text-navy tracking-tight">{s.value}</p>
                <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.15em] text-gray-500">{s.label}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16">
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 bg-brand text-white text-sm font-semibold px-7 py-4 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#e0671a]"
            >
              Work with us <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
